import type { Feature, GeoJSON, MultiPolygon } from 'geojson';
import type { Sources } from 'mapbox-gl';
import { getAxiosClient } from './common';

type TGeom = GeoJSON.Polygon | GeoJSON.MultiPolygon | GeoJSON.MultiLineString;

const axios = getAxiosClient();

export interface Extent {
  xmin: number;
  ymin: number;
  xmax: number;
  ymax: number;
}

export async function printMap(
  layers: Sources,
  title: string,
  notes: string,
  printType: 'pdf' | 'png',
  layoutSize = 'A3',
  layoutOrientation = 'landscape',
  mapImage: string,
  geojson?: Feature[],
  ccEmails?: string[],
  legendHeadings?: Record<string, string>,
) {
  const { data, status } = await axios({
    method: 'POST',
    url: `/v2/misc/printMap`,
    data: {
      layers,
      title,
      notes,
      printType,
      layoutSize,
      layoutOrientation,
      mapImage,
      geojson,
      ccEmails,
      legendHeadings,
    },
  });
  return { data, status };
}

export async function sendEvent(eventType: string, detail: unknown = '', organizationId?: string) {
  await axios({
    method: 'POST',
    url: `/v1/misc/logEvent`,
    data: {
      eventType,
      detail,
      organizationId,
    },
  });
}

export async function get811Data(projectId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/misc/get811Data?projectId=${projectId}`,
  });

  return data;
}

export async function isUrbanArea(geometry: TGeom) {
  const { data } = await axios({
    method: 'POST',
    url: `/v2/misc/isUrbanArea`,
    data: {
      geometry,
    },
  });

  return data.isUrban;
}

export async function getParcelSuggestions(parcelId: string, countyId: string) {
  const { data } = await axios({
    method: 'GET',
    url: `/v2/misc/parcelSuggestions?parcelId=${parcelId}&countyId=${countyId}`,
  });

  return data;
}

export async function getProjectionList(lng: number, lat: number) {
  const { data } = await axios.get(`/v2/misc/getProjectionList?lng=${lng}&lat=${lat}`);

  return data;
}

export async function getAgenciesSummary(boundary: MultiPolygon) {
  const { data } = await axios.post('/v2/misc/getAgenciesSummary', boundary);
  return data;
}

export async function getZoningDetailsAtPoint(lng: number, lat: number) {
  const { data } = await axios.get(`/v2/misc/getZoningDetailsAtPoint?lng=${lng}&lat=${lat}`);
  return data;
}

export async function transcodeImage({
  url,
  projectId,
  width,
  height,
}: {
  url: string;
  projectId: string;
  width: number;
  height: number;
}): Promise<string> {
  const { data } = await axios.get(
    `/v2/misc/transcodeImage?url=${encodeURIComponent(url)}&projectId=${encodeURIComponent(projectId)}&width=${width}&height=${height}`,
    {
      headers: {
        'Cache-Control': 'public, max-age=300',
      },
    },
  );

  return data;
}

export async function validateGeometry<T = GeoJSON>(
  geometry: T,
): Promise<{ isValid: boolean; isValidReason: string; geometry: T }> {
  const { data } = await axios.post('/v2/misc/validateGeometry', { geometry });
  return data;
}
